// imports
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import { ICON, IMAGE } from '../../assets';
import CollectButton from '../button/collect';
import { LINKS, ROUTES } from '../../constants';
import { DATE_UTILS } from '../../common/utils';
import { ITraitMatching } from '../../common/types';
import { HeroNFT } from '../../repositories/web3/types';

// interfaces / types / consts
interface Props {
  nft: HeroNFT;
  disabled?: boolean;
  selected?: boolean;
  isCrewMember?: boolean;
  type?: 'collection' | 'generic';
  traitMatching?: ITraitMatching[];
  onDrag?: () => void;
  onClicked: () => void;
}

// exports
export default function HeroCard(props: Props): JSX.Element {
  const {
    type, selected, isCrewMember, traitMatching, disabled,
    nft: {
      tokenId, image, name, locked, chain, lockedUntil
    }, onDrag, onClicked
  } = props;
  // hooks configuration
  const navigate = useNavigate();
  const [timer, setTimer] = useState(DATE_UTILS.formatRemainingTime(lockedUntil));
  // componentDidMount
  useEffect(() => {
    if (timer !== '0s') {
      let timeout = 1;
      if (timer.includes('d') && timer.includes('h')) {
        timeout = 60 * 60;
      } else if (timer.includes('h') && timer.includes('m')) {
        timeout = 60;
      }
      setTimeout(() => {
        setTimer(DATE_UTILS.formatRemainingTime(lockedUntil));
      }, timeout * 1000);
    }
  }, [timer, lockedUntil]);
  // render
  return (
    <button className="hero-card-container" type="button" disabled={disabled} onClick={onClicked} onDragStart={onDrag}>
      <div
        draggable={!disabled}
        id={`hero-traits-${tokenId}`}
        className={clsx('card', type === 'generic' && selected && 'selected', type === 'generic' && (isCrewMember) && 'outline')}
      >
        {type === 'collection' && !locked && chain !== 'Transit' && (
          <div className="switch-container">
            <img className="switch" src={(chain === 'L1') !== selected ? ICON.SWITCH_OFF : ICON.SWITCH_ON} alt="switch" />
          </div>
        )}
        {(isCrewMember) && <img className="crew-checkmark" src={ICON.HERO_CHECKMARK} alt="checkmark" />}
        {locked && (
          <div className="overlay">
            {timer !== '0s' && (
              <div className="timer">
                <span className="text">Time Remaining</span>
                <span className="time">{timer}</span>
              </div>
            )}
            <img className="lock" src={ICON.STAKED} alt="locked" />
          </div>
        )}
        <div className="body">
          <LazyLoadComponent placeholder={<img className="avatar" src={IMAGE.HERO_PLACEHOLDER} alt="avatar" />}>
            <img
              alt="avatar"
              draggable={false}
              src={image || IMAGE.HERO_PLACEHOLDER}
              className={clsx('avatar', !locked && 'sheen-overlay')}
              onError={({ currentTarget }) => { currentTarget.src = IMAGE.HERO_PLACEHOLDER; }}
            />
          </LazyLoadComponent>
          {(locked && timer === '0s') ? (
            <div className="collect-btn">
              <CollectButton onClick={() => navigate(ROUTES.QUESTS, { state: { defaultTab: 'completed' } })} />
            </div>
          ) : (
            <div>
              <div className="name truncate-text">{name}</div>
              <div className="traits-container info">
                <div className="id">{`#${`${tokenId}`.padStart(4, '0')}`}</div>
                {type === 'collection' ? (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${LINKS.OPEN_SEA_COLLECTION_IMMORTALS}/${tokenId}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img className="opensea-icon" src={ICON.OPEN_SEA} alt="opensea" />
                  </a>
                ) : (
                  <div className="traits">
                    {traitMatching?.map((trait) => (
                      <div key={trait.icon} className={`trait ${trait.status}`}><img src={ICON[trait.icon]} alt="trait" /></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// default props
HeroCard.defaultProps = {
  disabled: false,
  selected: false,
  type: 'generic',
  traitMatching: [],
  onDrag: undefined,
  isCrewMember: false
};
